import { Injectable } from '@angular/core';
import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import {
  connector,
  environment,
  prefixClient,
  prefixCustoms,
  prefixFreight,
  prefixIdentity,
  prefixInsurance,
  provider,
} from '@env/environment';
import { CompaniesService } from '@services/companies.service';
import { GenericService } from '@services/generic.service';
import { CustomEventLogsRepository } from '@app/data-layer/repositories';
import { CustomEventLogsModel } from '@app/data-layer/models/custom-event-logs';
import { Observable } from 'rxjs';

@Injectable()
export class LoggerService {
  private appInsights: ApplicationInsights;

  constructor(
    private genericService: GenericService,
    private companiesService: CompaniesService,
    private customEventLogsRepository: CustomEventLogsRepository,
  ) {
    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: environment.appInsightsKey,
        enableAutoRouteTracking: true,
      },
    });
    this.appInsights.loadAppInsights();
    this.loadCustomTelemetryProperties();
  }

  public logPageView(name?: string, url?: string): void {
    // option to call manually
    this.appInsights.trackPageView({
      name: name,
      uri: url,
    });
  }

  public logEvent(name: string, properties?: { [key: string]: any }): void {
    this.appInsights.trackEvent({ name: name }, properties);
  }

  public logCustomEvent(log: CustomEventLogsModel): Observable<never> {
    const timestamp = new Date().toISOString();
    return this.customEventLogsRepository.logEvent({
      ...log,
      data: {
        ...log.data,
        customData: {
          ...log.data.customData,
          timestamp,
        },
      },
    });
  }

  public logMetric(name: string, average: number, properties?: { [key: string]: any }): void {
    this.appInsights.trackMetric({ name: name, average: average }, properties);
  }

  public logException(exception: Error, severityLevel: number = 1): void {
    this.appInsights.trackException({ exception: exception, severityLevel: severityLevel });
  }

  public logTrace(message: string, properties?: { [key: string]: any }): void {
    this.appInsights.trackTrace({ message: message }, properties);
  }

  public setAuthenticatedUserContext(authenticatedUserId: string, accountId?: string, storeInCookie: boolean = false): void {
    this.appInsights.setAuthenticatedUserContext(authenticatedUserId, accountId, storeInCookie);
  }

  public clearAuthenticatedUserContext(): void {
    this.appInsights.clearAuthenticatedUserContext();
  }

  private loadCustomTelemetryProperties(): void {
    this.appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
      const target: string = envelope.baseData.target;
      const url: string = envelope.baseData.uri;
      const info: { [key: string]: any } = envelope.data;
      const localHostUrl: string = 'localhost';
      const urls: string[] = [
        prefixCustoms,
        prefixIdentity,
        prefixClient,
        prefixFreight,
        prefixInsurance,
        connector,
        provider,
        localHostUrl,
      ];

      // To set custom properties:
      if (!!this.companiesService.companyExternalIds || !!this.companiesService.userExternalId) {
        envelope.data.externalCompanyId = this.companiesService.companyExternalIds?.externalCompanyId;
        envelope.data.externalUserId = this.companiesService.userExternalId;
      }
      envelope.data.clientVersion = this.genericService.appVersion$?.getValue()?.version;

      // see https://stackoverflow.com/questions/51993221/block-some-endpoints-not-to-be-send-to-application-insights-nodejs
      // https://docs.microsoft.com/en-us/azure/azure-monitor/app/javascript#telemetry-initializers
      // if URL is outside our domain or the log is not hardcoded - skip it

      return (
        (!!target && !!urls.find((item: string) => target.includes(item))) ||
        (!!url && !!urls.find((item: string) => url.includes(item))) ||
        !!info?.isShouldSend
      );
    });
  }
}
